import Session from './session';
import User from './vlUser';
import {
  parse,
  lclString,
  lclFlexRightFill,
  lclTextAlignmentClass,
  lclFlexRow,
  lclDirection,
  isHebrew,
  updateUserLanauge,
} from '../utils/vlStrings';
import * as utils from '../utils/rhutils';
import VLUser from './vlUser';

const PromotionType = Object.freeze({
  CREDIT: 0,
  PERSONA: 1,
  PERCENT: 2,
  GIFT_FROM_PERSONA: 3,
  AUTO_GENERATED_DISCOUNT: 4,
});

interface Dictionary<T> {
  [Key: string]: T;
}

class VLPromotion {
  constructor(id: string, user_id: string, rewarder_id: string) {
    this.id = id;
    this.user_id = user_id;
    this.rewarder_id = rewarder_id;
  }

  id = ''; //the unique id of the promotion
  /// String representing the object’s type. Objects of the same type share the same value.
  object?: string;

  /// The code that can activate this promotion
  code?: string;

  /// The id of the user who created this promotion (optional - might be sensitive for most user's on client)
  user_id?: string;

  /// The id of the rewarder (can be auto-generated and hence id might be 'system'
  rewarder_id: string; //system | user_id

  /// Raw type of the promotion
  typeRaw: number = 0; //credit = 0, persona = 1

  /// One of forever, once, and repeating. Describes how long a customer who applies this coupon will get the discount.
  duration: 'once' | 'repeating' | 'forever' = 'once'; // "once", "repeating", "forever"

  /// Title name of the promotion like "SPECIAL DEAL" (optional)
  name?: string;

  /// Taking account of the above properties, whether this coupon can still be applied to a customer.
  valid: boolean = false;

  /// Title text of the promotion (optional)
  title?: string;

  /// Detail text of the promotion (optional)
  detail?: string;

  /// Subtitle text of the promotion (optional)
  subtitle?: string;

  /// Custom image of the promotion (optional)
  image_url?: string;

  /// Custom button title for promotion (optional)
  button_title?: string;

  /// When this promotion was created
  created_on: number = new Date().getTime();

  /// When this promotion expires (optional)
  expiration_date?: number;

  /// The id of the "Creator" this relats to
  creator_id?: User['id'];

  /// If amount_off has been set, the three-letter ISO code for the currency of the amount to take off.
  currency?: string;

  /// Absolute amount off current price - for example, 5 ($5), 0.5 (50c). -1.0 means not set
  amount_off?: number = -1.0;

  /// Percentage discounts in percentage, from 0 to 1 (0.3 = 33%)
  percent_off?: number = 0.0;

  /// TNumber of times this coupon has been applied to a customer.
  times_redeemed: number = 0;

  /// Maximum number of times this coupon can be redeemed, in total, across all customers, before it is no longer valid.
  max_redemptions: number = 0; //0 = unlimited

  /// Set of key - value pairs that you can attach to an object. This can be useful for storing additional information about the object in a structured format.
  metadata: Dictionary<string> = {};

  /// Is this promotion applicable to the business pricing of a cretor
  is_business = false;

  hasExpired(): boolean {
    if (!this.expiration_date) {
      return true;
    }
    return Date.now() > this.expiration_date;
  }

  /// Simple function that takes a price and currency and returns updated price based on promotion params
  /// - Parameters:
  ///   - originalPrice: The original price to apply the promotion to (what the user sees)
  ///   - originalCurrency: The original currency to apply the promotion to (what the user sees)
  /// - Returns: An updated price based on promotion (might stay same if promotion is invalid)
  updatedPrice(originalPrice: number, originalCurrency: string): number {
    if (this.amount_off && this.currency) {
      let amountOffInUserCurrency = utils.convertToPrice(
        this.amount_off!,
        this.currency,
        originalCurrency
      );
      return Math.max(0, originalPrice - amountOffInUserCurrency);
    } else if (this.percent_off && this.percent_off > 0) {
      return Math.max(0, originalPrice - originalPrice * this.percent_off!);
    } else {
      return originalPrice;
    }
  }

  toUpdatedPromotionPrice(creator: VLUser): number {
    let price = this.is_business ? creator.priceBusiness : creator.price;
    if (this.amount_off && this.currency) {
      return Math.max(0, price - this.amount_off!);
    } else if (this.percent_off && this.percent_off > 0) {
      return Math.max(0, price - price * this.percent_off!);
    } else {
      return price;
    }
  }

  static fromJSON(json: any) {
    if (!json.id || !json.user_id || !json.rewarder_id) {
      return null;
    }

    var promotion = new VLPromotion(json.id, json.user_id, json.rewarder_id);
    //required
    promotion.created_on = json.created_on;
    promotion.detail = json.detail;
    //optional
    promotion.code = json.code;
    promotion.subtitle = json.subtitle;
    promotion.title = json.title;
    promotion.amount_off = json.amount_off ? json.amount_off : -1.0;
    promotion.button_title = json.button_title;
    promotion.percent_off = json.percent_off
      ? json.percent_off
      : promotion.percent_off;
    promotion.is_business = json.is_business ?? false;

    return promotion;
  }
}

export default VLPromotion;
